import { useState, useEffect } from "react"
import { useQuery, keepPreviousData } from "@tanstack/react-query"
import { Link } from "wouter"
import { MapPin, Search, ChevronLeft, ChevronRight } from "lucide-react"
import { useTranslation } from "react-i18next"
import { api } from "@/lib/api"
import { FindMyBookingDialog } from "@/components/FindMyBookingDialog"
import type { PaginatedArealList } from "@fohet/caravan-booker-shared/types"

const PER_PAGE = 12

export const ArealSelectionPage = () => {
    const { t, i18n } = useTranslation()
    const [searchInput, setSearchInput] = useState("")
    const [q, setQ] = useState("")
    const [page, setPage] = useState(1)
    const [showRecoverDialog, setShowRecoverDialog] = useState(false)

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setQ(searchInput)
            setPage(1)
        }, 300)
        return () => clearTimeout(timer)
    }, [searchInput])

    const { data, isLoading, error } = useQuery({
        queryKey: ["areals", "public", i18n.language, q, page],
        queryFn: () => {
            const params = new URLSearchParams({
                locale: i18n.language,
                page: String(page),
                per_page: String(PER_PAGE),
            })
            if (q) params.set("q", q)
            return api.get<PaginatedArealList>(`/areals/public?${params.toString()}`)
        },
        placeholderData: keepPreviousData,
    })

    const areals = data?.data ?? []
    const total = data?.total ?? 0
    const totalPages = Math.max(1, Math.ceil(total / PER_PAGE))

    return (
        <div>
            <div className="mb-8">
                <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900 mb-3">
                    {t("guest.areal.heading")}
                </h1>
                <p className="text-slate-500 font-bold text-lg mb-6">
                    {t("guest.areal.subtitle")}
                </p>

                {/* Search */}
                <div className="relative max-w-md">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-4 text-slate-400" />
                    <input
                        type="text"
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        placeholder={t("guest.areal.search")}
                        className="w-full bg-white border border-slate-200 rounded-2xl py-3 pl-10 pr-4 font-bold text-sm focus:outline-none focus:ring-2 focus:ring-brand shadow-sm"
                    />
                </div>
            </div>

            {isLoading && !data && (
                <div className="flex items-center justify-center py-24">
                    <div className="text-slate-400 font-bold">{t("guest.areal.loading")}</div>
                </div>
            )}

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-red-700 font-bold">
                    {t("guest.areal.error")}
                </div>
            )}

            {!isLoading && !error && areals.length === 0 && (
                <div className="text-center py-24">
                    <p className="text-slate-400 font-bold text-lg">
                        {q ? t("guest.areal.noResults") : t("guest.areal.empty")}
                    </p>
                </div>
            )}

            {areals.length > 0 && (
                <>
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {areals.map((areal) => (
                            <Link key={areal.id} to={`/a/${areal.slug}`}>
                                <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl overflow-hidden transition-all duration-200 hover:shadow-2xl hover:-translate-y-1 cursor-pointer active:scale-95">
                                    <div className="h-48 bg-slate-100 overflow-hidden">
                                        {areal.coverPhoto ? (
                                            <img
                                                src={areal.coverPhoto.url}
                                                alt={areal.coverPhoto.altText ?? areal.name}
                                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                                                <span className="text-4xl">🏕️</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="p-6">
                                        <h2 className="text-xl font-black text-slate-900 mb-1">{areal.name}</h2>
                                        <div className="flex items-center gap-1 text-slate-500 font-bold text-sm mb-3">
                                            <MapPin className="size-3.5" />
                                            <span>{areal.city}, {areal.country}</span>
                                        </div>
                                        {areal.description && (
                                            <p className="text-slate-600 text-sm font-bold line-clamp-2 mb-4">
                                                {areal.description}
                                            </p>
                                        )}
                                        <div className="flex items-center justify-between">
                                            <div className="flex flex-col gap-0.5">
                                                <span className="text-xs font-black uppercase tracking-widest text-slate-400">
                                                    {t("guest.areal.spotsCount", { count: areal.spotCount })}
                                                </span>
                                                <span className="text-xs font-bold text-brand">
                                                    {t("guest.areal.availableToday", { count: Math.max(0, areal.spotCount - areal.bookedToday) })}
                                                </span>
                                            </div>
                                            <span className="bg-brand text-white text-xs font-black rounded-xl px-3 py-1">
                                                {t("guest.areal.bookNow")}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-3 mt-10">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                className="flex items-center gap-1.5 bg-slate-100 text-slate-600 font-bold rounded-2xl px-4 py-3 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                            >
                                <ChevronLeft className="size-4" />
                                {t("guest.areal.prevPage")}
                            </button>
                            <span className="text-sm font-black text-slate-500">
                                {t("guest.areal.pageOf", { page, total: totalPages })}
                            </span>
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages}
                                className="flex items-center gap-1.5 bg-slate-100 text-slate-600 font-bold rounded-2xl px-4 py-3 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                            >
                                {t("guest.areal.nextPage")}
                                <ChevronRight className="size-4" />
                            </button>
                        </div>
                    )}
                </>
            )}
            <div className="mt-12 pt-6 border-t border-slate-100 text-center">
                <button
                    onClick={() => setShowRecoverDialog(true)}
                    className="text-sm font-bold text-slate-400 hover:text-slate-600 transition-colors"
                >
                    {t("guest.recover.link")}
                </button>
            </div>

            <FindMyBookingDialog open={showRecoverDialog} onClose={() => setShowRecoverDialog(false)} />
        </div>
    )
}
